import type { RewardGrantDto } from '@codeframe/api-contract';
import { useQueryClient } from '@tanstack/react-query';
import { Cpu, Sparkles, Unlock } from 'lucide-react';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { keys } from '../api/hooks.js';
import { Badge, Button, Modal } from '../components/ui.js';
import s from './rewards.module.css';

interface RewardsContextValue {
  announce: (rewards: RewardGrantDto | null | undefined, title?: string) => void;
}

const RewardsContext = createContext<RewardsContextValue>({ announce: () => undefined });

export function useRewards(): RewardsContextValue {
  return useContext(RewardsContext);
}

const FEATURE_ROUTES: Record<string, string> = { workshop: '/workshop', arena: '/arena', academy: '/academy' };

/** Celebrates XP, level-ups, new hardware and newly unlocked game systems. */
export function RewardsProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<{ rewards: RewardGrantDto; title: string } | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const announce = useCallback(
    (rewards: RewardGrantDto | null | undefined, title = 'Rewards') => {
      if (!rewards) return;
      if (rewards.parts.length > 0) void qc.invalidateQueries({ queryKey: keys.parts });
      setCurrent({ rewards, title });
    },
    [qc],
  );
  const r = current?.rewards;
  const leveled = r && r.levelAfter > r.levelBefore;
  const disciplines = r ? Object.entries(r.disciplineXp).filter(([, xp]) => xp > 0) : [];
  return (
    <RewardsContext.Provider value={{ announce }}>
      {children}
      {r && (
        <Modal
          title={leveled ? `LEVEL ${r.levelAfter}` : current.title}
          onClose={() => setCurrent(null)}
          footer={
            <>
              {r.parts.length > 0 && !(r.newFeatures[0] && FEATURE_ROUTES[r.newFeatures[0].id]) && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setCurrent(null);
                    navigate('/frame');
                  }}
                >
                  Open Frame bay
                </Button>
              )}
              {r.newFeatures[0] && FEATURE_ROUTES[r.newFeatures[0].id] && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setCurrent(null);
                    navigate(FEATURE_ROUTES[r.newFeatures[0]!.id]!);
                  }}
                >
                  Open {r.newFeatures[0].name}
                </Button>
              )}
              <Button onClick={() => setCurrent(null)}>Continue</Button>
            </>
          }
        >
          <div className={s.body}>
            {leveled && (
              <div className={s.levelUp}>
                <Sparkles size={22} />
                <div>
                  <div className={s.levelTitle}>Pilot level up</div>
                  <div className="muted">
                    Level {r.levelBefore} → {r.levelAfter}
                  </div>
                </div>
              </div>
            )}
            <div className={s.rows}>
              {r.playerXp > 0 && <Row label="Player XP" value={`+${r.playerXp}`} />}
              {r.programmingXp > 0 && <Row label="Programming XP" value={`+${r.programmingXp}`} />}
              {r.languageXp > 0 && <Row label="Python mastery" value={`+${r.languageXp}`} />}
              {r.frameXp > 0 && <Row label="Frame XP" value={`+${r.frameXp}`} />}
              {disciplines.map(([id, xp]) => (
                <Row key={id} label={id.replace(/_/g, ' ')} value={`+${xp}`} />
              ))}
            </div>
            {r.parts.length > 0 && (
              <div className={s.unlocks}>
                {r.parts.map((p) => (
                  <div key={p.id} className={s.unlock}>
                    <Cpu size={16} />
                    <div>
                      <strong>{p.name}</strong>
                      <div className="muted">New hardware. Install it on your Frame in the Frame bay.</div>
                    </div>
                    <Badge tone="ok">New</Badge>
                  </div>
                ))}
              </div>
            )}
            {r.newFeatures.length > 0 && (
              <div className={s.unlocks}>
                {r.newFeatures.map((f) => (
                  <div key={f.id} className={s.unlock}>
                    <Unlock size={16} />
                    <div>
                      <strong>{f.name} unlocked</strong>
                      <div className="muted">{f.description}</div>
                    </div>
                    <Badge tone="ok">New</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </RewardsContext.Provider>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={s.row}>
      <span className="label">{label}</span>
      <span className="mono">{value}</span>
    </div>
  );
}
